// Count & Scan: walk the shelves with the camera open — scan a box, type how many, Save & next.
// A barcode nobody knows yet: pick the part once and the barcode is saved on it (the next scan finds it).
// Used in the office (Inventory → Count) and on the phone (roles with "Counts inventory"). No prices, no system quantity.
import { useState } from 'react';
import { api, useApi, type Row } from '../lib';
import { Button, LiveScanner, SearchBox, PhotoThumbs, Icon, useToast, useDebounced } from '../ui';
import { partPhotos } from './Inventory';

type Step = { s: 'scan' } | { s: 'find'; code: string } | { s: 'count'; part: Row; code?: string };

export function ScanCount({ onSaved }: { onSaved?: () => void }) {
  const toast = useToast();
  const [step, setStep] = useState<Step>({ s: 'scan' });
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const search = useApi<Row>(step.s === 'find' && dq.trim().length >= 2 ? `/api/count/find?q=${encodeURIComponent(dq)}` : null, [dq, step.s]);
  const [n, setN] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ name: string; sku: string; n: number }[]>([]);

  async function scanned(code: string) {
    try {
      const r = await api<Row>(`/api/count/find?q=${encodeURIComponent(code)}`);
      const p = (r.parts as Row[])[0];
      if (r.exact && p) { setStep({ s: 'count', part: p }); setN(''); }
      else { setStep({ s: 'find', code }); setQ(''); }
    } catch (e) { toast((e as Error).message, 'err'); setStep({ s: 'scan' }); }
  }
  async function pick(p: Row) {
    const code = step.s === 'find' ? step.code : '';
    if (code) {
      try { await api(`/api/parts/${p.id}/barcode`, { body: { code } }); toast(`Barcode saved on ${p.sku} — next time it comes up by itself`); }
      catch (e) { toast((e as Error).message, 'err'); }
    }
    setStep({ s: 'count', part: p, code }); setN('');
  }
  async function save() {
    if (step.s !== 'count' || n === '') return;
    setBusy(true);
    try {
      await api(`/api/count/${step.part.id}`, { method: 'PUT', body: { counted: Number(n) } });
      setDone(d => [{ name: step.part.name, sku: step.part.sku, n: Number(n) }, ...d.filter(x => x.sku !== step.part.sku)]);
      toast(`${step.part.sku}: ${n} saved`); onSaved?.();
      setStep({ s: 'scan' }); setN('');
    } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); }
  }

  return (
    <div className="scan-count">
      {step.s === 'scan' && <>
        <LiveScanner key={done.length} onCode={c => void scanned(c)} hint="Scan the barcode on the box — then type how many are on the shelf." />
        <Button className="full" onClick={() => { setStep({ s: 'find', code: '' }); setQ(''); }}>No barcode — find the part</Button>
      </>}

      {step.s === 'find' && <div className="sc-find">
        {step.code ? <div className="note-bar"><Icon name="alert" size={16} /><span>New barcode <b>{step.code}</b> — find the part it belongs to. It's saved on that part once.</span></div>
          : <p className="muted small">Type the part #, name or bin.</p>}
        <SearchBox value={q} onChange={setQ} placeholder="Part #, name or bin…" />
        <ul className="line-list">{((search.data?.parts || []) as Row[]).map(p => (
          <li key={p.id} className="clickable" onClick={() => void pick(p)}><PhotoThumbs ids={partPhotos(p)} />
            <span className="grow"><b>{p.name}</b><small>{p.sku}{p.bin ? ` · Bin ${p.bin}` : ''}</small></span>{p.counted !== null && p.counted !== undefined && <span className="stock">✓ {p.counted}</span>}</li>))}
          {search.data && !(search.data.parts as Row[]).length && <li className="muted">Not found — add it in Inventory first.</li>}</ul>
        <Button onClick={() => setStep({ s: 'scan' })}>← Back to scanning</Button>
      </div>}

      {step.s === 'count' && <div className="count-card">
        <PhotoThumbs ids={partPhotos(step.part)} />
        <div className="grow"><b>{step.part.name}</b><small className="muted block">{step.part.sku}{step.part.bin ? ` · Bin ${step.part.bin}` : ''}{step.part.uom && step.part.uom !== 'each' ? ` · count in ${step.part.uom}` : ''}</small>
          {step.part.counted !== null && step.part.counted !== undefined && <small className="muted block">Already counted: {step.part.counted} — saving replaces it</small>}</div>
        <input className="count-n" inputMode="numeric" autoFocus value={n} onChange={e => setN(e.target.value.replace(/\D/g, '').slice(0, 7))} placeholder="How many?" aria-label="How many are on the shelf" onKeyDown={e => { if (e.key === 'Enter') void save(); }} />
        <div className="actions-row"><Button kind="primary" className="big" busy={busy} disabled={n === ''} onClick={() => void save()}>Save & next</Button><Button onClick={() => { setStep({ s: 'scan' }); setN(''); }}>Skip</Button></div>
      </div>}

      {done.length > 0 && <>
        <h4 className="form-sub">Counted now ({done.length})</h4>
        <ul className="line-list">{done.map(d => <li key={d.sku}><span className="grow"><b>{d.name}</b><small>{d.sku}</small></span><span className="stock">{d.n}</span></li>)}</ul>
      </>}
    </div>
  );
}
